import React, { useState, useEffect } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { Button, Row, Col, Card, Accordion } from 'react-bootstrap';

import { FormikInput, FormikCheckBox, FormikRangePicker, NestedAttributes } from '../../components';
import QuestionFields from './QuestionFields';
import SurveyPreview from './SurveyPreview';
import '../../services/yupCustomMethods';

const SurveyForm = ({
  errors,
  isSubmitting,
  setFieldValue,
  setFieldTouched,
  touched,
  values,
  submitCount,
  isValid
}) => {
  const { startDate, endDate, questionsAttributes } = values.survey;
  const [togglePreview, setTogglePreview] = useState(false);
  const [generalOpen, setGeneralOpen] = useState(true);
  const [preguntasOpen, setPreguntasOpen] = useState(true);

  const generalFields = ['name', 'startDate', 'endDate', 'introText', 'endText', 'description'];
  const questionFields = ['questionsAttributes'];

  const hasError = fields => fields.some(field => getIn(errors, `survey.${field}`));

  const generalHasErrors = hasError(generalFields);
  const questionsHasErrors = hasError(questionFields);

  useEffect(() => {
    if (submitCount > 0 && !isValid) {
      if (generalHasErrors) setGeneralOpen(true);
      if (questionsHasErrors) setPreguntasOpen(true);
    }
  }, [submitCount, isValid, generalHasErrors, questionsHasErrors]);

  const addQuestion = () => {
    const mapResults = questionsAttributes.map((body, index) => {
      if (body._destroy) return undefined;
      return (
        <Col md={11} className="bg-light round-10 ml-3 mr-n3 mb-3">
          <QuestionFields
            key={`question-${index.toString()}`}
            index={index}
            valuePath={`survey[questionsAttributes][${index}]`}
            questionAttribute={body}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
          />
        </Col>
      );
    });

    mapResults.push(
      <Col md={11} className="bg-light round-10 ml-3 mr-n3 mb-3">
        <Row className="p-3 pb-4">
          <Col md={3} className="sample-row">
            <FormikInput label="Tipo de Pregunta" disabled />
          </Col>
          <Col md={3} className="sample-row">
            <FormikInput label="Formato de Respuesta" disabled />
          </Col>
        </Row>
      </Col>
    );

    return (
      <>
        <NestedAttributes
          removeFirstItem
          mapInputs={mapResults}
          arrayValues={questionsAttributes}
          setFieldValue={setFieldValue}
          valuePath="survey[questionsAttributes]"
          newAttributes={{
            questionType: '',
            responseFormat: '',
            label: '',
            description: '',
            required: false,
            position: '',
            questionChoicesAttributes: []
          }}
        />
      </>
    );
  };

  return (
    <Form className="container-fluid">
      {togglePreview ? (
        <SurveyPreview survey={values.survey} employeeSurvey={{ employeeQuestionChoicesAttributes: [] }} />
      ) : (
        <>
          {/* Accordion: Datos Generales */}
          <div className="yellow-wrapper">
            <Accordion activeKey={generalOpen ? 'general' : null}>
              <Col className={`${generalOpen ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
                <Accordion.Toggle
                  as={Col}
                  eventKey="general"
                  className="card-header-title"
                  onClick={() => setGeneralOpen(!generalOpen)}
                  style={{ cursor: 'pointer' }}
                >
                  <span>Datos Generales</span>
                </Accordion.Toggle>
              </Col>

              <Accordion.Collapse eventKey="general">
                <Card>
                  <Card.Body>
                    <Row>
                      <Col md={4} className="pr-5">
                        <Field name="survey[name]">
                          {({ field }) => (
                            <FormikInput
                              {...field}
                              abbr
                              label="Nombre"
                              error={getIn(errors, field.name)}
                              touched={getIn(touched, field.name)}
                            />
                          )}
                        </Field>
                      </Col>
                      <Col md={4} className="pr-5">
                        <Field name="rangeDate">
                          {({ field }) => (
                            <FormikRangePicker
                              {...field}
                              abbr
                              minCurrentDate
                              startDateName="survey[startDate]"
                              endDateName="survey[endDate]"
                              startDate={startDate}
                              endDate={endDate}
                              showClearDates
                              error={getIn(errors, field.name)}
                              touched={getIn(touched, field.name)}
                            />
                          )}
                        </Field>
                      </Col>
                      <Col md={4} className="pr-5 d-flex align-items-center mt-3">
                        <Field name="survey[isAnonymous]">
                          {({ field }) => (
                            <FormikCheckBox {...field} field={field} type="switch" label="¿Es Anónima?" />
                          )}
                        </Field>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12} className="pr-5">
                        <Field name="survey[introText]">
                          {({ field }) => (
                            <FormikInput
                              {...field}
                              as="textarea"
                              rows={4}
                              label="Mensaje de Bienvenida"
                              error={getIn(errors, field.name)}
                              touched={getIn(touched, field.name)}
                            />
                          )}
                        </Field>
                      </Col>
                      <Col md={12} className="pr-5">
                        <Field name="survey[endText]">
                          {({ field }) => (
                            <FormikInput
                              {...field}
                              as="textarea"
                              rows={4}
                              label="Mensaje de Salida"
                              error={getIn(errors, field.name)}
                              touched={getIn(touched, field.name)}
                            />
                          )}
                        </Field>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12} className="pr-5">
                        <Field name="survey[description]">
                          {({ field }) => (
                            <FormikInput
                              {...field}
                              as="textarea"
                              rows={4}
                              label="Descripción"
                              error={getIn(errors, field.name)}
                              touched={getIn(touched, field.name)}
                            />
                          )}
                        </Field>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Accordion.Collapse>
            </Accordion>

            {/* Accordion: Preguntas */}
            <Accordion className="my-2" activeKey={preguntasOpen ? 'preguntas' : null}>
              <Col className={`${preguntasOpen ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
                <Accordion.Toggle
                  as={Col}
                  eventKey="preguntas"
                  className="card-header-title"
                  onClick={() => setPreguntasOpen(!preguntasOpen)}
                  style={{ cursor: 'pointer' }}
                >
                  <span>Preguntas</span>
                </Accordion.Toggle>
              </Col>

              <Accordion.Collapse eventKey="preguntas">
                <Card>
                  <Card.Body>{addQuestion()}</Card.Body>
                </Card>
              </Accordion.Collapse>
            </Accordion>
          </div>
        </>
      )}

      <Row className="d-flex justify-content-end my-5">
        <Col md={2}>
          <Button variant="warning" block onClick={() => setTogglePreview(!togglePreview)}>
            {togglePreview ? 'Editar' : 'Previsualizar'}
          </Button>
        </Col>
        <Col md={2}>
          <Button type="submit" block disabled={isSubmitting}>
            Guardar
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const setInitialValues = props => {
  const { survey } = props;
  return { survey, rangeDate: [survey.startDate, survey.endDate] };
};

const validationSchema = Yup.object().shape({
  survey: Yup.object().shape({
    name: Yup.string()
      .required('Debes ingresar un nombre')
      .max(120, 'Deben ser menos que 120 caracteres')
      .alphanumeric('Deben ser caracteres alfanuméricos'),
    startDate: Yup.date().formatdate(),
    endDate: Yup.date().formatdate(),
    description: Yup.string().max(3000, 'Máximo 3000 caracteres permitidos'),
    introText: Yup.string().max(3000, 'Máximo 3000 caracteres permitidos'),
    endText: Yup.string().max(3000, 'Máximo 3000 caracteres permitidos'),
    questionsAttributes: Yup.array().of(
      Yup.object().shape({
        label: Yup.string().required('Debes ingresar una pregunta'),
        questionType: Yup.string().required('Debes ingresar un tipo de pregunta'),
        questionChoicesAttributes: Yup.array().of(
          Yup.object().shape({
            label: Yup.string().nullable()
          })
        )
      })
    )
  }),
  rangeDate: Yup.array().rangedate(false)
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: false
})(SurveyForm);
